<script setup lang="ts">

import NotificationLayout from "@/Pages/notifications/layouts/NotificationLayout.vue";
import BaseLayout from "@/layouts/BaseLayout.vue";
import {DataTableHeader} from "@/types";
import {useUserAvatar} from "@/composables/avatar";
import {useDate} from "@/composables/date";
import {Head, router} from "@inertiajs/vue3";
import {route} from "ziggy-js";

interface Props {
    notifications: Array<any>
}

const props = defineProps<Props>();

defineOptions({
    layout: BaseLayout,
})

const { lastUsed } = useDate()

const headers: Array<DataTableHeader> = [
    {
        title: 'Status',
        value: 'status',
        width: '5%',
    },
    {
        title: 'Message',
        value: 'message',
        width: '55%',
    },
    {
        title: 'Sender',
        value: 'sender',
        width: '20%',
    },
    {
        title: 'Date',
        value: 'date',
        width: '20%',
    }
]

const goToNotification = (item: any) => {
    router.put(route('unread_notifications.update', item.id), {}, {
        onSuccess: () => {
            router.visit(item.data.url)
        }
    })
}
</script>

<template>
    <Head title="Notifications" />
    <notification-layout>
        <h1 class="font-weight-medium text-h5 mt-5">
            {{ route().current('unread_notifications.index') ? 'Unread' : 'All' }}
        </h1>
        <v-divider class="my-5" />
        <v-card
            v-if="notifications.length === 0"
            :flat="true"
        >
            <v-card-title class="text-center text-h5">
                All caught up!
            </v-card-title>
            <v-card-subtitle class="text-center">
                You have no new notifications.
            </v-card-subtitle>
        </v-card>
        <v-data-table
            v-if="notifications.length > 0"
            :headers="headers"
            :items="props.notifications"
            class="border rounded-lg cursor-pointer"
            :hover="true"
            no-data-text="You're all caught up! No new notifications."
        >
            <template #[`item.status`]="{item}">
                <v-icon
                    v-if="!item.read_at"
                    color="primary"
                >
                    mdi-circle-medium
                </v-icon>
            </template>
            <template #[`item.message`]="{item}">
                <v-list-item
                    class="py-2"
                    :link="false"
                    @click="goToNotification(item)"
                >
                    <template #prepend>
                        <v-icon
                            class="mb-5"
                            size="small"
                            color="success"
                        >
                            mdi-email
                        </v-icon>
                    </template>
                    <v-list-item-subtitle>
                        {{ item.data.project }}
                    </v-list-item-subtitle>
                    <v-list-item-title>
                        {{ item.data.message }}
                    </v-list-item-title>
                </v-list-item>
            </template>
            <template #[`item.sender`]="{item}">
                <v-avatar
                    size="24"
                    rounded="xl"
                    class="border-sm"
                >
                    <v-img
                        :src="useUserAvatar(item.data.user)"
                        contain
                    />
                </v-avatar>
            </template>
            <template #[`item.date`]="{item}">
                <div class="text-body-2 text-grey-darken-1">
                    {{ lastUsed(item.created_at) }}
                </div>
            </template>
        </v-data-table>
    </notification-layout>
</template>

<style scoped>
:deep(thead) {
    display: none;
}

:deep(.v-data-table-footer) {
    display: none;
}

:deep(.v-list-item__prepend) {
    width: 30px;
}
</style>
